import { useCallback, useMemo, useRef, useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import dayjs from "dayjs";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";

import Modal from "../../../components/common/modal/Modal";
import ButtonComponent from "../../../components/common/button/Button";
import DataTable from "../../../components/common/datatable/DataTable";
import { Loader } from "../../../components/common/Loader";

const UnmappedPlantModal = ({
  open,
  handleClose,
  title,
  unmappedPlantData,
  loading,
  onRowSelected,
  onSelectionChanged,
  rowSelection,
  getRowStyle,
  handleCellValueChange,
  saveBtnDisabled,
  onSaveUnmappedPlants,
  userRole,
}) => {
  const [columnState, setColumnState] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState("");
  const [disableFilterBtn, setDisableFilterBtn] = useState(true);
  const [filteredTotalColumn, setFilteredTotalColumn] = useState(0);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "-";
    return dayjs(timestamp).format("DD-MMM-YYYY HH:mm:ss");
  };

  const renderExpandableText = (params, maxLength = 50) => {
    const text = params.value || ""; // The error details text
    const isLongText = text.length > maxLength;

    // Initialize the showFullText property for the row if not already set
    if (!params.node.showFullText) {
      params.node.showFullText = false;
    }

    const toggleText = () => {
      params.node.showFullText = !params.node.showFullText;
      params.api.refreshCells({ rowNodes: [params.node], force: true }); // Refresh the cell to update the UI
    };

    return isLongText ? (
      <div>
        {params.node.showFullText ? (
          <>
            <span>{text}</span>{" "}
            <button
              onClick={toggleText}
              style={{
                background: "none",
                border: "none",
                color: "#181d1f",
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: "bold",
              }}
            >
              Less
            </button>
          </>
        ) : (
          <>
            <span>{text.substring(0, maxLength)}...</span>{" "}
            <button
              onClick={toggleText}
              style={{
                background: "none",
                border: "none",
                color: "#181d1f",
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: "bold",
              }}
            >
              More
            </button>
          </>
        )}
      </div>
    ) : (
      <span>{text}</span>
    );
  };

  const columnData = useMemo(() => {
    return [
      {
        headerName: "Is New",
        field: "isNew",
        sortable: true,
        resizable: false,
        filterParams: {
          textFormatter: (value) => {
            if (typeof value === "string") {
              const v = value.trim().toLowerCase();
              if (v === "!") return "!";
              if (v === "-") return "-";
              return v;
            }
            return value;
          },
        },
        valueGetter: (params) => {
          if (params.data?.isNew === true) return "!";
          if (params.data?.isNew === false) return "-";
          return "";
        },
        valueFormatter: (params) => params.value,
        cellRenderer: (params) => {
          if (params.value === "!") {
            return (
              <span
                style={{
                  fontWeight: "bold",
                  fontSize: "20px",
                  textAlign: "center",
                }}
              >
                !
              </span>
            );
          }
          if (params.value === "-") {
            return (
              <span
                style={{
                  color: "#616161",
                  fontWeight: "bold",
                  fontSize: "16px",
                }}
              >
                -
              </span>
            );
          }
          return "";
        },
        unSortIcon: true,
      },
      {
        headerName: "Ingredient",
        field: "ingredient",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        width: 150,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("ingredient"),
        },
      },
      {
        headerName: "S-Code",
        field: "specequivalentcode",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("specequivalentcode"),
        },
      },
      {
        headerName: "Year",
        field: "yearSIS",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("yearSIS"),
        },
      },
      {
        headerName: "Price Tag ID",
        field: "priceTagId",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("priceTagId"),
        },
      },
      {
        headerName: "Division",
        field: "division",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("division"),
        },
      },
      {
        headerName: "Change Date",
        field: "changeDate",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("changeDate"),
        },
      },
      {
        headerName: "Plant Id",
        field: "plantId",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("plantId"),
        },
      },
      {
        headerName: "Sap Plant Number",
        field: "sapPlantId",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        editable: true,
        cellEditor: "agTextCellEditor",
        cellClass: "cpsComments-cell",
        valueSetter: (params) => {
          if (params.newValue !== params.oldValue) {
            const dataCopy = { ...params.data, sapPlantId: params.newValue };
            params.node.setData(dataCopy);
            return true;
          }
          return false;
        },
        cellRenderer: (params) => {
          return (
            <div className="cps-comments-icon">
              <span style={{ flex: 1, padding: "0px 4px" }}>
                {params.value}{" "}
              </span>
              <EditIcon fontSize="sm" />
            </div>
          );
        },
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("sapPlantId"),
          "red-cell": (params) => {
            const value = params.value ? params.value.toString().trim() : "";
            return !value || value === "-";
          },
        },
      },
      {
        headerName: "Plant Name",
        field: "plantName",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("plantName"),
        },
      },
      {
        headerName: "Supplier Id",
        field: "supplierId",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("supplierId"),
        },
      },
      {
        headerName: "Sap Vendor Number",
        field: "sapSupplierId",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("sapSupplierId"),
        },
      },
      {
        headerName: "Sap Vendor Description",
        field: "sapSupplierDescription",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("sapSupplierDescription"),
        },
      },
      {
        headerName: "Supplier",
        field: "supplierName",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("supplierName"),
        },
      },
      {
        headerName: "Location Name",
        field: "locationName",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("locationName"),
        },
      },
      {
        headerName: "Supplier Country",
        field: "facilitycountry",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("facilitycountry"),
        },
      },
      {
        headerName: "PIN",
        field: "pin",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("pin"),
        },
      },
      {
        headerName: "Sap Material Number",
        field: "sapMaterialNumber",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("sapMaterialNumber"),
        },
      },
      {
        headerName: "Sap Material Description",
        field: "sapMaterialDescription",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        initialWidth: 180,
        wrapText: true,
        autoHeight: true,
        cellClass: "paymentTerms-cell",
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("sapMaterialDescription"),
        },
      },
      {
        headerName: "Material Group",
        field: "extmatgrp",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("extmatgrp"),
        },
      },
      {
        headerName: "Package Name",
        field: "packageName",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("packageName"),
        },
      },
      {
        headerName: "Package Size",
        field: "packSize",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("packSize"),
        },
      },
      {
        headerName: "Bulk Flag",
        field: "isbulk",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("isbulk"),
        },
      },
      {
        headerName: "UOM",
        field: "uom",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("uom"),
        },
      },
      {
        headerName: "Price per Kg",
        field: "pricePerKG",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("pricePerKG"),
        },
      },
      {
        headerName: "Currency",
        field: "currency",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("currency"),
        },
      },
      {
        headerName: "Effective From",
        field: "effectiveStartDate",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("effectiveStartDate"),
        },
      },
      {
        headerName: "Effective To",
        field: "effectiveEndDate",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("effectiveEndDate"),
        },
      },
      {
        headerName: "Allocation Quantity",
        field: "allocationQty",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("allocationQty"),
        },
      },
      {
        headerName: "Allocation Notes",
        field: "allocationNotes",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        initialWidth: 180,
        wrapText: true,
        autoHeight: true,
        cellClass: "paymentTerms-cell",
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("allocationNotes"),
        },
      },
      {
        headerName: "From (Qty)",
        field: "fromQty",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("fromQty"),
        },
      },
      {
        headerName: "To (Qty)",
        field: "toQty",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("toQty"),
        },
      },
      {
        headerName: "MOQ Comments",
        field: "moqComments",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        initialWidth: 180,
        wrapText: true,
        autoHeight: true,
        cellClass: "status-cell",
        cellRenderer: (params) => renderExpandableText(params, 50),
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("moqComments"),
        },
      },
      {
        headerName: "Payment Term",
        field: "paymentTerms",
        sortable: true,
        resizable: true,
        unSortIcon: true,
        initialWidth: 180,
        wrapText: true,
        autoHeight: true,
        cellClass: "paymentTerms-cell",
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("paymentTerms"),
        },
      },
      {
        headerName: "Manufacturing Lead Time",
        field: "manufacturingLeadTime",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("manufacturingLeadTime"),
        },
      },
      {
        headerName: "Auto Co Ship",
        field: "autoCoship",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("autoCoship"),
        },
      },
      {
        headerName: "Inco Terms",
        field: "incoterms",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("incoterms"),
        },
      },
      {
        headerName: "Named Place",
        field: "namedPlace",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("namedPlace"),
        },
      },
      {
        headerName: "Supply Point Number",
        field: "supplypointid",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("supplypointid"),
        },
      },
      {
        headerName: "Supply Point Description",
        field: "supplypointname",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        initialWidth: 180,
        wrapText: true,
        autoHeight: true,
        cellClass: "paymentTerms-cell",
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("supplypointname"),
        },
      },
      {
        headerName: "Manager Initials",
        field: "managerInitials",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("managerInitials"),
        },
      },
      {
        headerName: "Manager",
        field: "managerName",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("managerName"),
        },
      },
      {
        headerName: "PC Initial",
        field: "pcInitial",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("pcInitial"),
        },
      },
      {
        headerName: "SCA",
        field: "sca",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("sca"),
        },
      },
      {
        headerName: "PMU Date",
        field: "pmudate",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("pmudate"),
        },
        valueFormatter: (params) => formatTimestamp(params.value),
      },
      {
        headerName: "Price Per Lb",
        field: "pricePerLb",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("pricePerLb"),
        },
      },
      {
        headerName: "Manufacturing Facility Number",
        field: "facilitynum",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("facilitynum"),
        },
      },
      {
        headerName: "Manufacturing Facility Description",
        field: "facilityname",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        initialWidth: 180,
        wrapText: true,
        autoHeight: true,
        cellClass: "paymentTerms-cell",
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("facilityname"),
        },
      },
      {
        headerName: "Religious Attributes",
        headerClass: "center-header",
        children: [
          {
            headerName: "Halal",
            field: "halal",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              "bold-cell": (params) =>
                params.data.changedFieldNames?.includes("halal"),
            },
          },
          {
            headerName: "Kosher PO",
            field: "kosherPO",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              "bold-cell": (params) =>
                params.data.changedFieldNames?.includes("kosherPO"),
            },
          },
          {
            headerName: "Kosher Yr",
            field: "kosherYr",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              "bold-cell": (params) =>
                params.data.changedFieldNames?.includes("kosherYr"),
            },
          },
          {
            headerName: "Kosher",
            field: "kosherstatus",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              "bold-cell": (params) =>
                params.data.changedFieldNames?.includes("kosherstatus"),
            },
          },
          {
            headerName: "Kosher Passover",
            field: "kosherkfpner",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              "bold-cell": (params) =>
                params.data.changedFieldNames?.includes("kosherkfpner"),
            },
          },
          {
            headerName: "Kosher Passover (OU)",
            field: "kosherkfpou",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              "bold-cell": (params) =>
                params.data.changedFieldNames?.includes("kosherkfpou"),
            },
          },
          {
            headerName: "Kosher Year (NER)",
            field: "kosherkyrner",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              "bold-cell": (params) =>
                params.data.changedFieldNames?.includes("kosherkyrner"),
            },
          },
          {
            headerName: "Kosher Year (OU)",
            field: "kosherkyrou",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              "bold-cell": (params) =>
                params.data.changedFieldNames?.includes("kosherkyrou"),
            },
          },
          {
            headerName: "Allergen",
            field: "allergenstatus",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              "bold-cell": (params) =>
                params.data.changedFieldNames?.includes("allergenstatus"),
            },
          },
          {
            headerName: "Allergen Description",
            field: "allergenknown",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              "bold-cell": (params) =>
                params.data.changedFieldNames?.includes("allergenknown"),
            },
          },
          {
            headerName: "Fairtrade",
            field: "fairtradecertified",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              "bold-cell": (params) =>
                params.data.changedFieldNames?.includes("fairtradecertified"),
            },
          },
        ],
      },
      {
        headerName: "Forecast",
        field: "foreCastFile",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("foreCastFile"),
        },
      },
      {
        headerName: "Remarks",
        field: "mappingRemarks",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        initialWidth: 180,
        wrapText: true,
        autoHeight: true,
        // cellClass: "paymentTerms-cell",
        cellRenderer: (params) => renderExpandableText(params, 50),
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("mappingRemarks"),
        },
      },
      {
        headerName: "Status",
        pinned: "right",
        field: "status",
        sortable: true,
        resizable: true,
        unSortIcon: true,
        initialWidth: 180,
        wrapText: true,
        autoHeight: true,
        cellClass: "status-cell",
        cellClassRules: {
          "bold-cell": (params) =>
            params.data.changedFieldNames?.includes("status"),
        },
      },
    ];
  }, [unmappedPlantData]);

  const pagination = true;
  const paginationPageSize = 30;
  const paginationPageSizeSelector = [30, 50, 100, 500, 2000];

  const selectionColumnDef = useMemo(
    () => ({
      pinned: "left",
    }),
    []
  );

  const gridApiRef = useRef(null); // Reference to the grid API

  const onColumnChange = (e) => {
    const col = e.target.value;
    setSelectedColumn(col);
    if (col && gridApiRef.current) {
      gridApiRef.current.ensureColumnVisible(col);
    }
  };

  const onGridReady = (params) => {
    gridApiRef.current = params.api; // Store the grid API instance

    const savedFilterModel = localStorage.getItem("unmappedPlantsFilterModel");
    if (savedFilterModel) {
      params.api.setFilterModel(JSON.parse(savedFilterModel));
      setDisableFilterBtn(true);
    }
    // Optionally, force filter to apply if data is already loaded
    params.api.onFilterChanged();

    if (columnState) {
      params.columnApi.applyColumnState({
        state: columnState,
        applyOrder: true,
      });
    }
  };

  const clearAllFilters = () => {
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel(null); // Clear all filters
      localStorage.removeItem("unmappedPlantsFilterModel");
      setDisableFilterBtn(true);
    }
  };

  const onFilterChanged = (params) => {
    if (gridApiRef.current) {
      const filterModel = gridApiRef.current.getFilterModel();
      setDisableFilterBtn(Object.keys(filterModel).length === 0); // Enable button if filters exist
      setFilteredTotalColumn(Object.keys(filterModel).length);
      localStorage.setItem(
        "unmappedPlantsFilterModel",
        JSON.stringify(filterModel)
      );
    }
  };

  const onColumnMoved = useCallback((params) => {
    if (
      params &&
      params.columnApi &&
      typeof params.columnApi.getColumnState === "function"
    ) {
      setColumnState(params.columnApi.getColumnState());
    }
  }, []);

  const gridOptions = {
    getRowId: (params) => {
      return `${params?.data?.runOutputSeqId}`;
    },
    overlayLoadingTemplate: `<span class="ag--overlay-loading-center" >Please wait while data is loading</span>`,
    overlayNoRowsTemplate: `<span class="ag--overlay-no-rows-center" >No Data Available</span>`,
    defaultColDef: {
      sortable: true,
      filter: "agMultiColumnFilter",
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: 1.5,
        display: "flex",
        alignItems: "center",
      },
      valueGetter: (params) => {
          if (!params.data) return "";

          const field = params.colDef.field;
          const value = params.data[field];

          return value === null || value === undefined || value === "" ? "" : value;
      },
    },
    onColumnMoved,
    onFilterChanged,
  };

  const getContextMenuItems = (params) => {
    const defaultItems = params.defaultItems || [];

    // Remove the 'export' option
    return defaultItems.filter(item => item !== 'export');
  };

  return (
    <Modal
      open={open}
      handleClose={handleClose}
      title={title}
      fullWidth={true}
      maxWidth="xl"
      fullScreen={true}
      height={'100%'}
      content={
        loading ? (
          <Loader />
        ) : (
          <>
            <div
              style={{
                display: "flex",
                marginBottom: "20px",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <FormControl sx={{ m: 1, minWidth: 200 }} size="small">
                <InputLabel>Jump to Column</InputLabel>
                <Select
                  id="col-select"
                  value={selectedColumn}
                  label="Jump to Column"
                  onChange={onColumnChange}
                >
                  <MenuItem value="">Select Column</MenuItem>
                  {columnData?.map((col) => (
                    <MenuItem key={col?.field} value={col?.field}>
                      {col?.headerName || col?.field}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <ButtonComponent
                maxWidth="150px"
                label="Clear All Filters"
                bgColor="black"
                color="white"
                disabled={disableFilterBtn}
                onClick={clearAllFilters}
              />
            </div>
            <DataTable
              columns={columnData}
              rows={unmappedPlantData || []}
              paginationProp={pagination}
              paginationPageSize={paginationPageSize}
              paginationPageSizeSelector={paginationPageSizeSelector}
              onRowSelected={onRowSelected}
              selectionColumnDef={selectionColumnDef}
              rowSelection={rowSelection}
              onSelectionChanged={onSelectionChanged}
              onGridReady={onGridReady}
              gridOptions={gridOptions}
              getRowStyle={getRowStyle}
              onCellValueChanged={handleCellValueChange}
              filteredTotalColumn={filteredTotalColumn}
              getContextMenuItems={getContextMenuItems}
            />
          </>
        )
      }
      action={
        userRole !== "Viewer" &&
        <>
          <ButtonComponent
            maxWidth="170px"
            label="Cancel"
            bgColor="white"
            color="black"
            onClick={handleClose}
          />
          <ButtonComponent
            maxWidth="170px"
            label="Save"
            bgColor="black"
            color="white"
            disabled={saveBtnDisabled}
            onClick={onSaveUnmappedPlants}
          />
        </>
      }
    />
  );
};

export default UnmappedPlantModal;
